import './stepperV2.less'
import Stepper, { StepperProps } from './stepper'
import FlowControl, { FlowControlProps } from './flowControl'
import CustomLoader from '../Loader/loader'
import PopupConfirm from '../PopupConfirm/popupConfirm'
import { Breadcrumb, BreadcrumbItem, Button, Col } from '@lightbeamai/design-system'
import React, { PropsWithChildren, ReactNode, useState } from 'react'
import { Portal } from 'semantic-ui-react'
import classNames from 'classnames'
import { SignOut } from 'phosphor-react'
import { FormattedMessage, useIntl } from 'react-intl'
import { noop } from 'underscore'

export type StepperV2FlowControlProps = Omit<
  FlowControlProps,
  'isLastStep' | 'isFirstStep' | 'showConfirmCancel'
>

interface StepperV2Props {
  isFullScreen?: boolean
  showConfirmClose?: boolean
  showStepper?: boolean
  titleActions?: ReactNode
  showControlFlow?: boolean
  breadcrumbs?: BreadcrumbItem[]
  stepperProps: StepperProps
  onExit?: () => void
  flowControlProps: StepperV2FlowControlProps
  showLoader?: boolean
  className?: string
  wide?: boolean
}

const StepperV2 = (props: PropsWithChildren<StepperV2Props>) => {
  const {
    isFullScreen,
    showStepper = true,
    titleActions,
    showControlFlow = true,
    showConfirmClose = true,
    showLoader = false,
    children,
    breadcrumbs,
    className,
    stepperProps,
    flowControlProps,
    onExit,
    wide = false
  } = props
  const intl = useIntl()
  const ComponentTag = isFullScreen ? Portal : 'div'
  const componentProps = isFullScreen ? { open: true } : {}

  const [confirmGoBack, setConfirmGoBack] = useState(false)

  const { onClose, ...restStepperProps } = stepperProps

  const showConfirmCancel = () => {
    setConfirmGoBack(true)
  }

  const toggleConfirmCancel = () => setConfirmGoBack(!confirmGoBack)

  return (
    <ComponentTag {...componentProps}>
      <div
        className={classNames('stepper-v2-wrapper', {
          'full-screen': isFullScreen,
          [className as string]: !!className?.trim()
        })}
      >
        <div className="stepper-v2-header-wrapper">
          {breadcrumbs && breadcrumbs?.length > 0 && (
            <div className="stepper-v2-sub-header xs-py-8 xs-px-32">
              <Breadcrumb items={breadcrumbs} />
            </div>
          )}
          <div className="stepper-header">
            <Stepper
              showStepper={showStepper}
              {...restStepperProps}
              onClose={onClose ? (showConfirmClose ? showConfirmCancel : onClose) : undefined}
              titleActions={titleActions}
            />
          </div>
        </div>
        <div className="stepper-body">
          {onExit && (
            <div className="exit-button">
              <Button onClick={showConfirmCancel} variant="link">
                <SignOut className="exit-icon" />
                {intl.formatMessage({ id: 'exit' })}
              </Button>
            </div>
          )}

          <div className="step-details custom-scrollbars xs-pb-40">
            <Col span={wide ? 12 : 8} offset={wide ? 0 : 2}>
              {children}
            </Col>
          </div>
        </div>
        {showControlFlow && (
          <FlowControl
            isLastStep={stepperProps.activeStep === stepperProps.steps.length - 1}
            isFirstStep={stepperProps.activeStep === 0}
            showConfirmCancel={showConfirmClose ? showConfirmCancel : noop}
            {...flowControlProps}
          />
        )}
        <CustomLoader showLoader={showLoader} />
        <PopupConfirm
          open={confirmGoBack}
          onConfirm={() => {
            setConfirmGoBack(false)
            flowControlProps.onCancel?.()
          }}
          onCancel={toggleConfirmCancel}
          className="flow-control-confirm"
          header={
            <FormattedMessage id={flowControlProps?.confirmCancelHeaderKey ?? 'areYouSure'} />
          }
          content={flowControlProps?.confirmCancelContent ?? <></>}
          cancelButton={flowControlProps?.cancelCancelButtonText}
          confirmButton={flowControlProps?.cancelConfirmButtonText}
        />
      </div>
    </ComponentTag>
  )
}

export default StepperV2
